import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const productId = (item) => Number(item?.id ?? item?.product_id ?? item?.product?.id);
const productName = (item) => item?.name ?? item?.product?.name ?? `Product ${productId(item)}`;
const supplierId = (item) => {
  const id = item?.supplier?.id ?? item?.supplier_id;
  return id === undefined || id === null || id === "" ? "" : String(id);
};
const supplierName = (item) => item?.supplier?.name ?? item?.supplier_name ?? "";

/** SOF-312: the suppliers present in the product catalogue, in first-seen order. */
export const buildSupplierGroups = (catalog = []) => {
  const groups = new Map();
  catalog.forEach((item) => {
    const key = supplierId(item);
    if (!groups.has(key)) groups.set(key, { id: key, name: supplierName(item) || "No supplier", products: [] });
    groups.get(key).products.push(item);
  });
  return Array.from(groups.values());
};

const matchesSearch = (term, ...values) =>
  values.some((value) => String(value || "").toLowerCase().includes(term));

const ProductPickerControls = ({ catalog = [], value = [], onChange }) => {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const selectedIds = useMemo(() => new Set(value.map(productId)), [value]);
  const available = catalog.filter((item) => Number.isInteger(productId(item)) && !selectedIds.has(productId(item)));

  const addProduct = () => {
    const id = Number(selectedId);
    const qty = Number(quantity);
    if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(qty) || qty < 1 || qty > 1000) return;
    onChange([...value, { id, qty }]);
    setSelectedId("");
    setQuantity("1");
  };

  return (
    <div className="d-flex gap-2 align-items-end mb-3">
      <label className="flex-grow-1">
        Product
        <select className="form-control" value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
          <option value="">{t("Select a product")}</option>
          {available.map((item) => <option key={productId(item)} value={productId(item)}>{productName(item)}</option>)}
        </select>
      </label>
      <label>
        Quantity
        <input className="form-control" type="number" min="1" max="1000" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
      </label>
      <button className="btn btn-primary" type="button" onClick={addProduct}>{t("Add product")}</button>
    </div>
  );
};

/**
 * SOF-312: the products dropdown on the middle screen. The supplier list opens
 * first; choosing a supplier opens that supplier's products, and both levels
 * carry a search bar.
 */
export const SubscriptionProductsPicker = ({ catalog = [], value = [], onChange }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const term = search.trim().toLowerCase();
  const suppliers = useMemo(() => buildSupplierGroups(catalog), [catalog]);
  const visibleSuppliers = useMemo(
    () => (term ? suppliers.filter((supplier) => matchesSearch(term, supplier.name)) : suppliers),
    [suppliers, term],
  );
  const activeSupplier = suppliers.find((supplier) => supplier.id === selectedSupplier);
  const supplierProducts = useMemo(() => {
    if (!activeSupplier) return [];
    return term
      ? activeSupplier.products.filter((item) => matchesSearch(term, productName(item)))
      : activeSupplier.products;
  }, [activeSupplier, term]);

  return (
    <div className="subscription-products-picker">
      <input
        type="text"
        className="form-control search-input mb-3"
        placeholder={activeSupplier ? t("Search products") : t("Search suppliers")}
        aria-label={activeSupplier ? t("Search products") : t("Search suppliers")}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {activeSupplier ? (
        <>
          <div className="d-flex align-items-center mb-3">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => {
                setSelectedSupplier("");
                setSearch("");
              }}
            >
              <i className="fal fa-chevron-left me-1"></i>
              {t("Suppliers")}
            </button>
            <span className="fw-semibold">{activeSupplier.name}</span>
          </div>
          {supplierProducts.length === 0 ? <div className="fw-bold p-3">{t("No data found")}</div> : null}
          <ProductPickerControls catalog={supplierProducts} value={value} onChange={onChange} />
        </>
      ) : visibleSuppliers.length === 0 ? (
        <div className="fw-bold p-3">{t("No data found")}</div>
      ) : (
        <ul className="list-unstyled mb-0">
          {visibleSuppliers.map((supplier) => (
            <li key={supplier.id || "none"}>
              <button
                type="button"
                className="btn btn-link p-0 mb-2 text-start"
                onClick={() => {
                  setSelectedSupplier(supplier.id);
                  setSearch("");
                }}
              >
                {supplier.name} <span className="text-muted">({supplier.products.length})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** SOF-312: the products included in the subscription, shown on the right screen. */
export const SubscriptionProductIncludedList = ({ catalog = [], value = [], onChange }) => {
  const { t } = useTranslation();
  if (value.length === 0) return <div className="fw-bold p-3">{t("No product added")}</div>;
  return (
    <div className="subscription-products-included">
      {value.map((item) => {
        const catalogItem = catalog.find((candidate) => productId(candidate) === productId(item));
        return (
          <div className="d-flex justify-content-between align-items-center mb-2" key={productId(item)}>
            <span>{productName(catalogItem || item)} × {item.qty ?? item.quantity ?? 1}</span>
            <button type="button" className="btn btn-link" onClick={() => onChange(value.filter((candidate) => productId(candidate) !== productId(item)))}>{t("Remove")}</button>
          </div>
        );
      })}
    </div>
  );
};

const SubscriptionProductsField = ({ catalog = [], value = [], onChange, mode = "full" }) => {
  if (mode === "picker") {
    return <SubscriptionProductsPicker catalog={catalog} value={value} onChange={onChange} />;
  }
  if (mode === "included") {
    return (
      <SubscriptionProductsIncludedList
        catalog={catalog}
        products={value}
        onRemove={onChange}
      />
    );
  }
  return (
    <div className="subscription-products-field">
      <h4 className="fw-semibold mb-2">Products Included</h4>
      <ProductPickerControls catalog={catalog} value={value} onChange={onChange} />
      <SubscriptionProductIncludedList catalog={catalog} value={value} onChange={onChange} />
    </div>
  );
};

export const SubscriptionProductUsageTable = ({ products = [] }) => {
  if (!Array.isArray(products) || products.length === 0) return null;
  return (
    <div className="mb-2">
      <h6>Products Included</h6>
      <table className="table mb-0">
        <thead><tr><th>Product</th><th>Qty</th><th>Used</th><th>Available</th></tr></thead>
        <tbody>
          {products.map((item, index) => (
            <tr key={item.id ?? item.product_id ?? index}>
              <td>{productName(item)}</td>
              <td>{item.quantity ?? item.qty ?? 0}</td>
              <td>{item.used_quantity ?? item.used_qty ?? 0}</td>
              <td>{item.pending_quantity ?? item.remaining_quantity ?? item.pending_qty ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SubscriptionProductsField.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["full", "picker", "included"]),
};

SubscriptionProductsPicker.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
};

SubscriptionProductIncludedList.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
};

ProductPickerControls.propTypes = {
  catalog: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
};

/**
 * SOF-311 read-only side of the product picker: what the subscription already
 * includes, with the quantity and a remove action.
 */
export const SubscriptionProductsIncludedList = ({
  products = [],
  catalog = [],
  onRemove,
  title = "Products Included",
}) => {
  const rows = Array.isArray(products) ? products : [];
  const canRemove = typeof onRemove === "function";

  return (
    <div className="subscription-products-included mb-3" data-testid="subscription-products-included">
      <h6 className="fw-semibold mb-2">{title}</h6>
      {rows.length === 0 ? (
        <div className="fw-bold p-1">No product added</div>
      ) : (
        <ul className="list-unstyled mb-0">
          {rows.map((item) => {
            const id = Number(item?.id ?? item?.product_id);
            const catalogItem = catalog.find((candidate) => Number(candidate?.id ?? candidate?.product_id) === id);
            const name = catalogItem?.name ?? item?.name ?? item?.product?.name ?? `Product ${id}`;
            return (
              <li className="d-flex justify-content-between align-items-center mb-2" key={id}>
                <span>
                  {name} &times; {item?.qty ?? item?.quantity ?? 1}
                </span>
                {canRemove ? (
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    aria-label={`Remove ${name}`}
                    onClick={() => onRemove(rows.filter((candidate) => Number(candidate?.id ?? candidate?.product_id) !== id))}
                  >
                    <i className="fal fa-times"></i>
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

SubscriptionProductsIncludedList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  catalog: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func,
  title: PropTypes.node,
};

SubscriptionProductUsageTable.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
};

export default SubscriptionProductsField;
